import React, { useEffect, useState } from 'react';
import '../App.css';
import { slugify } from '../utils/slugify';
import { useNavigate, Link } from 'react-router';
import { useSession } from '../Cart-Context/session.js'; 
import { fetchCartProducts, updateItem, clearCart } from '../Cart-Context/context.js';
import { trackEvent } from '../utils/trackEvents'; 
import { getProductImage } from '../utils/imageHelper.js';

const Cart = () => {
  // Pulling both sessionId and experimentId from the session context
  const { sessionId, experimentId } = useSession(); 
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0.00);
  const navigate = useNavigate();

  const currentUser = localStorage.getItem("user");

  // Load products on mount
  useEffect(() => {
    if (sessionId || currentUser) {
      loadProducts();
    }
    window.addEventListener("cartUpdated", loadProducts);
    return () => window.removeEventListener("cartUpdated", loadProducts);
}, [sessionId, currentUser]);

  // Track when the user arrives at the Cart page - Added experimentId
  useEffect(() => {
    trackEvent(sessionId, 'PAGE_VIEW', 'Cart Page', {}, experimentId);
  }, [sessionId, experimentId]);

  // Recalculate total whenever the products array updates
  useEffect(() => {
    const calculateTotal = () => {
      let price = 0;
      products.forEach(product => {
          // Explicitly convert to Number to avoid string/math weirdness
          const unitPrice = Number(product.item.price?.toString().replace('$', '')) || 0;
          price += (unitPrice * product.quantity);
      });
      setTotal(price.toFixed(2));
    };
    calculateTotal();
  }, [products]);

  if (!sessionId && !currentUser) {
    return (
      <main className="page-container">
        <div className="empty-cart-message">
          <h2>Please login or start a session to access your cart.</h2>
          <button className="shop-now-btn" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </main>
    );
  }

  const totalQuantity = products.reduce((acc, product) => acc + product.quantity, 0);

  async function loadProducts() {
    try {
      const productsData = await fetchCartProducts();
      setProducts(productsData);
    } catch (err) {
      console.error("Error loading cart products:", err);
    }
  }

  const handleEmptyCart = async () => {
    if (window.confirm("Are you sure you want to remove all items from your cart?")) {
      // Track the interaction before clearing
      trackEvent(sessionId, 'CLICK', 'Empty Cart Button', { itemsRemoved: totalQuantity }, experimentId);
      
      const success = await clearCart();
      if (success) {
        setProducts([]);
      }
    }
  };

  async function handleMinusQuantity(quantity, product) {
    // Track the quantity reduction - Added experimentId
    trackEvent(sessionId, 'CLICK', 'Decrease Quantity', { 
        productId: product.item.id, 
        productName: product.item.name,
        newQuantity: quantity - 1 
    }, experimentId);

    if (quantity - 1 === 0) {
      await updateItem(1, product.item.id, 3);
    } else {
      await updateItem(quantity - 1, product.item.id, 1);
    }
    loadProducts();
  }

  async function handlePlusQuantity(quantity, product) {
    // Track the quantity increase - Added experimentId
    trackEvent(sessionId, 'CLICK', 'Increase Quantity', { 
        productId: product.item.id, 
        productName: product.item.name,
        newQuantity: quantity + 1 
    }, experimentId);

    await updateItem(quantity + 1, product.item.id, 2);
    loadProducts();
  }

  return (
    <main className="page-container">
      <div className="cart-page-container">
        {/* Left Section: Items List */}
        <div className="cart-items-section">
          <div className="cart-header">
            <h1 className="page-title" style={{ margin: 0 }}>Shopping Cart</h1>
            {products.length > 0 && (
              <button className="empty-cart-btn" onClick={handleEmptyCart}>
                Empty Cart
              </button>
            )}
          </div>

          <div className="cart-grid">
            {products.length > 0 ? (
              products.map((product) => {
                const displayUnitPrice = Number(product.item.price?.toString().replace('$', '')).toFixed(2);
                const itemSubtotal = (displayUnitPrice * product.quantity).toFixed(2);

                return (
                  <div className="cart-item-card" key={product.cartProductId}>
                    <Link
                      className="cart-item-link"
                      to={`/product/${product.item.id}/${slugify(product.item.name)}`}
                      onClick={() => trackEvent(sessionId, 'CLICK', 'Cart Item Link', { productName: product.item.name }, experimentId)}
                    >
                      {product.item.image && (
                        <img src={getProductImage(product.item.image)} alt={product.item.name} />
                      )}
                      <div className="cart-item-details">
                        <h3>{product.item.name}</h3>
                        <p className="cart-item-price">
                          <strong>${itemSubtotal}</strong>
                          {product.quantity > 1 && (
                            <span className="unit-price-breakdown"> 
                               (${displayUnitPrice} ea.)
                            </span>
                          )}
                        </p>
                      </div>
                    </Link>

                    <div className="quantity-controls">
                      <button className="qty-btn" onClick={() => handleMinusQuantity(product.quantity, product)}>-</button>
                      <span className="qty-number">{product.quantity}</span>
                      <button className="qty-btn" onClick={() => handlePlusQuantity(product.quantity, product)}>+</button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-cart-message">
                <h2>Your cart is currently empty.</h2>
                <button className="shop-now-btn" onClick={() => navigate('/products')}>Go Shopping</button>
              </div>
            )}
          </div>
        </div>

        {/* Right Section: Order Summary Sidebar */}
        {products.length > 0 && (
          <aside className="cart-summary">
            <h2 className="summary-title">Order Summary</h2>
            <div className="summary-row">
              <span>Items ({totalQuantity})</span>
              <span>${total}</span>
            </div>
            <div className="summary-row total-row">
              <strong>Estimated Total</strong>
              <strong>${total}</strong>
            </div>
            <button
              className="checkout-btn"
              onClick={() => {
                trackEvent(sessionId, 'CLICK', 'Continue to Checkout Button', { 
                    finalTotal: total, 
                    finalItemCount: totalQuantity 
                }, experimentId);
          
                // This ensures the backend gets the correct ID (or RESEARCH_MODE) 
                // and prevents the 'sessionId cannot be null' error.
                navigate('/checkout', { state: { products, total, experimentId, sessionId } });
              }}
            >
              Continue To Checkout
            </button>
          </aside>
        )}
      </div>
    </main>
  );
};

export default Cart;